import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ChevronDown, ChevronUp } from 'lucide-react';
import InfoTooltip from '../../../components/InfoTooltip';
import SearchBar from '../../../components/SearchBar';
import TablePager from '../../../components/TablePager';
import { tableCellClass, tableHeaderCellClass } from '../../../components/TableCard';
import { makeOrganizationDetailUrl } from '../../../routes';
import { tenantSelector } from '../../../selector/authenticationSelector';
import {
    useGetCustomerClassesQuery,
    useGetOrganizationsQuery,
} from '../../../api/organization/organizationApiSlice';
import { Organization } from '../../../api/organization/organizationApiTypes';
import { ORGANIZATION_TYPE_LABEL_KEY } from '../../organizations/organizationTypeLabels';
import { formatDate } from '../../../utils';
import { cn } from '../../../utils/utils';
import { onRowClick } from '../../../utils/rowClick';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

type SortKey =
    | 'name'
    | 'type'
    | 'customerClass'
    | 'contact'
    | 'city'
    | 'units'
    | 'status'
    | 'created';
type SortOrder = 'asc' | 'desc';

const displayNameOf = (org: Organization): string => org.displayName?.trim() || org.legalName;

const sortAriaValue = (isActive: boolean, order: SortOrder): 'ascending' | 'descending' | 'none' => {
    if (!isActive) return 'none';
    return order === 'asc' ? 'ascending' : 'descending';
};

/**
 * Customer-organization list — the "Organizations" tab of the customers surface
 * (H-3b). Search + pagination are server-side (plan §5:
 * `?search=&page=&pageSize=`); column sorting is applied client-side over the
 * visible page (the contract has no sort param yet — a future enhancement).
 *
 * Rendered with the design-system table (card wrapper + plain `<table>`, shared
 * cell classes from `TableCard`) so it matches the sibling individual-customers
 * tab; sorting and pagination use plain controls and the shared `TablePager`.
 */
const OrganizationsTab: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const tenant = useSelector(tenantSelector);
    const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';

    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);
    const [sortKey, setSortKey] = useState<SortKey>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

    // Reset list state when the active tenant changes (CLAUDE.md: state tied to
    // a tenant resets on context switch). Guard on the id so first mount is a
    // no-op.
    const previousTenantRef = useRef(tenant);
    useEffect(() => {
        if (previousTenantRef.current !== tenant) {
            previousTenantRef.current = tenant;
            setSearch('');
            setPage(0);
        }
    }, [tenant]);

    // Wait for the tenant before firing (hard-reload race): on a full reload
    // the routes render before the tenant is restored into Redux, so an
    // immediate query goes out without X-Tenant — and when the tenant lands
    // moments later, tenantCacheResetMiddleware resets the api state while
    // the request is in flight, freezing the subscribed hook in isLoading
    // (the infinite-spinner bug). Skipping until the tenant exists makes the
    // first fetch start after the reset instead of racing it.
    const { data, isLoading, isFetching, isError } = useGetOrganizationsQuery({
        search: search.trim() || undefined,
        page: page + 1, // backend is 1-based (plan §5); the pager is 0-based
        pageSize,
    }, { skip: !tenant });
    const { data: customerClasses = [] } = useGetCustomerClassesQuery(undefined, { skip: !tenant });

    const classNameById = useMemo(() => {
        const map = new Map<string, string>();
        customerClasses.forEach((c) => map.set(c.id, c.name));
        return map;
    }, [customerClasses]);

    const items = data?.items ?? [];
    const total = data?.total ?? 0;

    // An organization with no class of its own is not "n/a" — it inherits the
    // tenant's system default, and that is the tariff its bookings actually
    // get. Showing `n/a` here while the detail form shows "Standard" would be
    // the same question answered two ways on two screens.
    const systemDefaultName = useMemo(
        () => customerClasses.find((c) => c.isSystemDefault && c.archivedAt === null)?.name,
        [customerClasses],
    );

    const classNameOf = (org: Organization): string =>
        (org.customerClassId && classNameById.get(org.customerClassId))
        || systemDefaultName
        || t('common.not_applicable');

    const sortedItems = useMemo(() => {
        const value = (org: Organization): string => {
            switch (sortKey) {
                case 'type': return t(ORGANIZATION_TYPE_LABEL_KEY[org.type]);
                case 'customerClass': return classNameOf(org);
                case 'contact': return org.primaryContact?.name ?? org.primaryContact?.email ?? '';
                case 'status': return t(`organization.status.${org.status}`);
                case 'city': return org.billingAddress?.city ?? '';
                // Sorting is client-side over the current page and compares
                // strings, so a raw number would order 10 before 2. Padded so
                // the units column sorts the way the eye expects.
                case 'units': return String(org.unitCount ?? 0).padStart(6, '0');
                case 'created': return org.createdAt;
                case 'name':
                default: return displayNameOf(org);
            }
        };
        const factor = sortOrder === 'asc' ? 1 : -1;
        return [...items].sort((a, b) => value(a).localeCompare(value(b)) * factor);
        // classNameOf depends on classNameById; included via items/customerClasses
    }, [items, sortKey, sortOrder, classNameById, t]);

    /**
     * No row menu (design-decisions.md › Zeilenaktionen in Tabellen, PO
     * 2026-11-03): a click on the row opens the organization, and the name is
     * a link for the keyboard. The menu held only "Details anzeigen", which
     * repeats the row click and changes nothing — and a row with nothing to
     * change gets no ⋮. PO, 2026-11-03: ersetzt die Entscheidung vom
     * 2026-08-21 (#2165 §4), the menu kept for consistency. Archiving stays on the detail page, where
     * the context for it is (running agreements, released rooms, members who
     * lose coverage).
     */

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortOrder('asc');
        }
    };

    // `help` only where the header alone does not say what the column is.
    // "Customer class" was the one column that meant nothing without knowing
    // it is the tariff axis (staging pass, 2026-08-19).
    const columns: { key: SortKey, label: string, help?: string }[] = [
        { key: 'name', label: t('organization.list.columns.name') },
        { key: 'type', label: t('organization.list.columns.type') },
        {
            key: 'customerClass',
            label: t('organization.list.columns.customer_class'),
            help: t('organization.help.customer_class'),
        },
        {
            key: 'contact',
            label: t('organization.list.columns.primary_contact'),
            help: t('organization.help.primary_contact'),
        },
        { key: 'city', label: t('organization.list.columns.city') },
        { key: 'units', label: t('organization.list.columns.units') },
        {
            key: 'status',
            label: t('organization.list.columns.status'),
            help: t('organization.help.status'),
        },
        { key: 'created', label: t('organization.list.columns.created') },
    ];

    let listContent: React.ReactNode;
    if (isError) {
        listContent = (
            <p className={'p-6 text-sm text-foreground/60'}>
                {t('organization.list.load_error')}
            </p>
        );
    } else if (isLoading || !tenant) {
        // !tenant: the boot phase before the tenant is restored — the query
        // hasn't started yet, so this is still "loading", not "empty".
        listContent = (
            <div className={'p-10 flex items-center justify-center'}>
                <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
            </div>
        );
    } else if (sortedItems.length === 0) {
        listContent = (
            <p className={'p-6 text-sm text-foreground/60'}>
                {t('organization.list.empty')}
            </p>
        );
    } else {
        listContent = (
            <>
                <div className={'overflow-x-auto'}>
                    <table className={'w-full text-sm'}>
                        <thead>
                            <tr className={'border-b border-foreground/10'}>
                                {columns.map((col) => {
                                    const isActive = sortKey === col.key;
                                    return (
                                        <th
                                            key={col.key}
                                            className={tableHeaderCellClass}
                                            aria-sort={sortAriaValue(isActive, sortOrder)}
                                        >
                                            <span className={'flex items-center gap-1.5'}>
                                                <button
                                                    type={'button'}
                                                    onClick={() => handleSort(col.key)}
                                                    className={'flex items-center gap-1 hover:text-foreground '
                                                        + 'transition-colors focus:outline-none focus:underline'}
                                                >
                                                    {col.label}
                                                    {isActive && (sortOrder === 'asc'
                                                        ? <ChevronUp className={'w-3.5 h-3.5'} />
                                                        : <ChevronDown className={'w-3.5 h-3.5'} />)}
                                                </button>
                                                {/* Outside the button: the tooltip is not a sort control. */}
                                                {col.help && <InfoTooltip text={col.help} />}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedItems.map((org) => (
                                <tr
                                    key={org.id}
                                    onClick={onRowClick(() => navigate(makeOrganizationDetailUrl(org.id)))}
                                    className={cn(
                                        'border-b border-foreground/5 last:border-b-0',
                                        'cursor-pointer hover:bg-foreground/5 transition-colors',
                                        org.archivedAt && 'opacity-50',
                                    )}
                                >
                                    <td className={tableCellClass}>
                                        <Link
                                            to={makeOrganizationDetailUrl(org.id)}
                                            className={'text-primary hover:underline'}
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            {displayNameOf(org)}
                                        </Link>
                                    </td>
                                    <td className={tableCellClass}>{t(ORGANIZATION_TYPE_LABEL_KEY[org.type])}</td>
                                    <td className={tableCellClass}>{classNameOf(org)}</td>
                                    <td className={tableCellClass}>
                                        {/* #2102 item 2 — the `owner` member.
                                            Name when the account has one, else
                                            the e-mail, which is always there
                                            and is what you would use anyway.
                                            A pending contact is marked: the
                                            address is named but that person
                                            has never signed in, so they cannot
                                            manage members yet (#2163). */}
                                        {org.primaryContact ? (
                                            <>
                                                {org.primaryContact.name || org.primaryContact.email}
                                                {org.primaryContact.pending && (
                                                    <span className={'ml-1 text-foreground/50'}>
                                                        {t('organization.list.contact_pending')}
                                                    </span>
                                                )}
                                                {/* #3037 — several contacts allowed; the
                                                    rest are starred in the member list. */}
                                                {(org.contactCount ?? 0) > 1 && (
                                                    <span className={'ml-1 text-foreground/50'}>
                                                        {t('organization.list.more_contacts', {
                                                            count: (org.contactCount ?? 0) - 1,
                                                        })}
                                                    </span>
                                                )}
                                            </>
                                        ) : (
                                            t('common.not_applicable')
                                        )}
                                    </td>
                                    <td className={tableCellClass}>
                                        {org.billingAddress?.city ?? t('common.not_applicable')}
                                    </td>
                                    <td className={tableCellClass}>
                                        {/* `n/a` when the field is absent, not
                                            0 — a response without it must not
                                            read as "this organization has no
                                            units". */}
                                        {org.unitCount ?? t('common.not_applicable')}
                                    </td>
                                    <td className={tableCellClass}>
                                        {t(`organization.status.${org.status}`)}
                                    </td>
                                    <td className={tableCellClass}>{formatDate(org.createdAt, language)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <TablePager
                    className={'border-t border-foreground/10'}
                    count={total}
                    page={page}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    onPageChange={setPage}
                    onRowsPerPageChange={(rowsPerPage) => {
                        setPageSize(rowsPerPage);
                        setPage(0);
                    }}
                />
            </>
        );
    }

    return (
        <div className={'flex flex-col gap-6'}>
            <SearchBar
                value={search}
                placeholder={t('organization.list.search_placeholder')}
                onChange={(val) => {
                    setSearch(val);
                    setPage(0);
                }}
            />

            <div className={'bg-text-contrast rounded-[5px] shadow-sm overflow-hidden'}>
                {listContent}
            </div>

            {isFetching && !isLoading && (
                <p className={'text-xs text-foreground/40'}>{t('common.loading')}</p>
            )}
        </div>
    );
};

export default OrganizationsTab;
